import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, ChevronLeft, ChevronRight, 
  Eye, Package, X,
  Maximize2, Minimize2
} from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SEO from '@/components/shared/SEO';
import { ProductSchema, BreadcrumbSchema } from '@/components/shared/JsonLdSchemas';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { products } from '@/data/products';
import { resolveRedirect } from '@/data/redirects';
import { reportRedirectHit } from '@/utils/redirectHit';
import {
  objectFitOverrides,
  imagePositionOverrides,
  generateGallery,
} from '@/pages/product/productImageHelpers';
import {
  ProductFeaturesTab,
  ProductSpecificationsTab,
  ProductLearnTab,
} from '@/pages/product/ProductDetailTabs';
import { groupSwatchesByColor, sortSwatchesByColorGroup } from '@/data/swatchColorGroups';
import { resolveDiscontinuedSwatch, normalizeColorSlug } from '@/data/discontinuedSwatches';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { AccessoriesSection } from '@/components/accessories';
import { IndustryQuoteModal } from '@/components/modals/IndustryQuoteModal';
import { hasResponsiveImages, getResponsiveImageProps } from '@/components/shared/ResponsiveProductImage';
import ZoomableImage from '@/components/shared/ZoomableImage';
import PlankZoomImage from '@/components/shared/PlankZoomImage';

// Image position overrides for specific gallery images (show floor/bottom instead of center)

// Images that should use object-contain instead of object-cover (show full image, not cropped)
const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isVisualizerOpen, setIsVisualizerOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedSwatch, setSelectedSwatch] = useState(null);
  const [isSwatchModalOpen, setIsSwatchModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Guard the discontinued-swatch effect so it only fires once per URL
  // visit — a rename inside the effect would otherwise re-trigger it.
  const discontinuedHandledFor = useRef(null);
  const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [isPlankFullscreen, setIsPlankFullscreen] = useState(false);
  const [sampleFormData, setSampleFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    sampleSet: '',
    notes: ''
  });

  const sampleSetOptions = [
    { id: 'single', label: '3"x5" Single Sample', description: 'Individual color sample' },
    { id: 'medium', label: '9"x9" Single Sample', description: 'Larger format sample' },
    { id: 'chainset', label: '3"x5" Full Chainset', description: 'Complete color collection' }
  ];

  // Find product and resolve inherited swatches from base product
  const product = useMemo(() => {
    const found = products.find(p => p.slug === slug);
    if (!found) return null;

    // If this product inherits swatches from a base product, resolve them
    if (found.inheritSwatchesFrom) {
      const sourceProduct = products.find(p => p.slug === found.inheritSwatchesFrom);
      if (sourceProduct) {
        const resolved = { ...found };
        const ownSwatches = resolved.swatches || [];
        const sourceSwatches = sourceProduct.swatches || [];
        // Merge: own swatches first, then source swatches not already present
        const ownNames = new Set(ownSwatches.map(s => s.name));
        resolved.swatches = [...ownSwatches, ...sourceSwatches.filter(s => !ownNames.has(s.name))];
        if (!resolved.fourUpSwatches && sourceProduct.fourUpSwatches) {
          resolved.fourUpSwatches = sourceProduct.fourUpSwatches;
        }
        return resolved;
      }
    }
    return found;
  }, [slug]);

  /**
   * Discontinued-swatch rescue: if a visitor lands with ?color=<slug>
   * that no longer exists on this product (per ``discontinuedSwatches``),
   * (a) toast a friendly notice, (b) select the replacement swatch, and
   * (c) swap the URL in-place so the share-link stays correct.
   *
   * Harmless when no color query is present or when the color is still
   * live on the product — it just no-ops.
   */
  useEffect(() => {
    if (!product) return;
    const incoming = searchParams.get("color");
    if (!incoming) return;
    const guardKey = `${slug}::${incoming}`;
    if (discontinuedHandledFor.current === guardKey) return;
    discontinuedHandledFor.current = guardKey;

    const incomingSlug = normalizeColorSlug(incoming);
    const swatchesList = product.swatches || [];
    const findBySlug = (needle) => swatchesList.findIndex(
      (s) => normalizeColorSlug(s.name) === needle,
    );

    const liveIndex = findBySlug(incomingSlug);
    if (liveIndex !== -1) {
      // Color is still on the product — open it on mount.
      setSelectedSwatch({ ...swatchesList[liveIndex], index: liveIndex });
      return;
    }

    const rescue = resolveDiscontinuedSwatch(slug, incoming);
    if (!rescue) return;
    const replacementIndex = findBySlug(rescue.replacement);
    if (replacementIndex === -1) return;

    toast.info(
      `${rescue.displayName} has been discontinued — showing ${rescue.replacementDisplayName} instead.`,
      { duration: 6000 },
    );
    setSelectedSwatch({
      ...swatchesList[replacementIndex],
      index: replacementIndex,
    });
    // Replace the stale color query so the URL stays shareable and
    // doesn't keep tripping the rescue on refresh.
    const next = new URLSearchParams(searchParams);
    next.set("color", rescue.replacement);
    setSearchParams(next, { replace: true });
  }, [product, slug, searchParams, setSearchParams]);

  // If product not found, first consult the legacy-redirect map — a bunch of
  // WordPress-era /products/... URLs 404 in GSC and need to route to the
  // current SKU or category page before we render a dead-end placeholder.
  // The redirect map is also consulted inside LegacyRedirectsGate for the
  // wildcard fallthrough; we call it here too because /products/:slug catches
  // single-segment legacy category paths before the wildcard runs. Both
  // call-sites report the hit to /api/redirect-hit so ops can see which
  // legacy URLs are still driving live traffic.
  if (!product) {
    const redirectTarget = resolveRedirect(location.pathname);
    if (redirectTarget) {
      reportRedirectHit(location.pathname, redirectTarget);
      return <Navigate to={`${redirectTarget}${location.search || ''}${location.hash || ''}`} replace />;
    }
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="font-heading text-3xl font-bold mb-4">Product Not Found</h1>
            <p className="text-muted-foreground mb-6">
              The product you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/products">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Products
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const gallery = generateGallery(product);
  
  // Generate dynamic description for SEO
  const productDescription = typeof product.description === 'string' 
    ? product.description 
    : (Array.isArray(product.description) ? product.description[0] : `${product.name} - premium commercial flooring from Portico Systems.`);

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % gallery.length);
  };

  return (
    <Layout>
      <SEO 
        title={product.name}
        description={`${productDescription.substring(0, 155)}...`}
        canonicalUrl={`/products/${product.slug}`}
        ogImage={product.heroImage || product.image}
      />
      <ProductSchema product={product} />
      <BreadcrumbSchema items={[
        { name: "Home", url: "/" },
        { name: "Products", url: "/products" },
        { name: product.name + (product.variant ? ` - ${product.variant}` : '') }
      ]} />
      {/* Hero Image */}
      <section className="relative h-64 md:h-80 lg:h-96 bg-muted">
        {hasResponsiveImages(product.slug) ? (
          <img
            {...getResponsiveImageProps(product.slug, "hero", product.heroImage || product.image, "(max-width: 768px) 100vw, 100vw")}
            alt={product.name}
            className={`w-full h-full ${(product.heroImage || product.image)?.endsWith('.svg') ? 'object-contain p-8 bg-white' : 'object-cover'}`}
          />
        ) : (
          <img
            src={product.heroImage || product.image}
            alt={product.name}
            className={`w-full h-full ${(product.heroImage || product.image)?.endsWith('.svg') ? 'object-contain p-8 bg-white' : 'object-cover'}`}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
      </section>

      {/* Breadcrumb and Back */}
      <section className="bg-background border-b border-border">
        <div className="container-portico py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-shop-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Products
            </button>
            <div className="text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/products" className="hover:text-foreground">Products</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground">{product.name}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Product Content */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column: Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Main Image */}
              <div 
                className="relative rounded-lg overflow-hidden bg-muted mb-4 cursor-pointer group"
                onClick={() => setIsImageModalOpen(true)}
                data-testid="main-product-image"
              >
                <img
                  src={gallery[selectedImageIndex]}
                  alt={`${product.name} - View ${selectedImageIndex + 1}`}
                  className={`w-full h-96 group-hover:scale-105 transition-transform duration-300 ${gallery[selectedImageIndex]?.endsWith('.svg') ? 'object-contain p-8 bg-white' : objectFitOverrides[gallery[selectedImageIndex]] === 'contain' ? 'object-contain bg-muted' : 'object-cover'}`}
                  style={imagePositionOverrides[gallery[selectedImageIndex]] ? { objectPosition: imagePositionOverrides[gallery[selectedImageIndex]] } : undefined}
                />
                
                {/* Expand hint */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                  <div className="bg-card/90 px-4 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    <span className="text-sm font-medium">Click to expand</span>
                  </div>
                </div>
                
                {/* Navigation Arrows */}
                {gallery.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-card/80 rounded-full flex items-center justify-center hover:bg-card transition-colors"
                      data-testid="gallery-prev-btn"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-card/80 rounded-full flex items-center justify-center hover:bg-card transition-colors"
                      data-testid="gallery-next-btn"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {gallery.map((img, idx) => (
                  <button
                    key={img}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={cn(
                      "flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all",
                      selectedImageIndex === idx 
                        ? "border-primary" 
                        : "border-transparent hover:border-border"
                    )}
                    data-testid={`gallery-thumb-${idx}`}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      className={`w-full h-full ${img?.endsWith('.svg') ? 'object-contain p-1 bg-white' : 'object-cover'}`}
                      style={imagePositionOverrides[img] ? { objectPosition: imagePositionOverrides[img] } : undefined}
                    />
                  </button>
                ))}
              </div>

              {/* Product Info Below Carousel */}
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">
                  {product.type}
                </p>
                <h1 className="font-heading text-2xl md:text-3xl font-bold mb-3" data-testid="product-title">
                  {product.name} {product.variant && <span className="text-muted-foreground font-normal">- {product.variant}</span>}
                </h1>
                <div className="text-muted-foreground space-y-4" data-testid="product-description">
                  {Array.isArray(product.description) ? (
                    product.description.map((para, idx) => (
                      <p key={`${idx}-${para.slice(0, 30)}`}>{para}</p>
                    ))
                  ) : (
                    <p>{product.description}</p>
                  )}
                </div>
              </div>

              {/* View In Space CTA - Hidden for accessories */}
              {product.categoryId !== "accessories" && (
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => setIsVisualizerOpen(true)}
                  data-testid="view-in-space-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View In Your Space
                </Button>
              )}
            </motion.div>

            {/* Right Column: Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {/* Single Plank Preview - For Plank products */}
              {product.singlePlankPreviews && product.singlePlankPreviews.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold mb-3">Single Plank Preview ({product.singlePlankPreviews.length})</h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-72 overflow-y-auto pr-2">
                    {product.singlePlankPreviews.map((preview, idx) => (
                      <button
                        key={preview.image || preview.name}
                        onClick={() => {
                          setSelectedSwatch({ ...preview, index: idx, isPlankPreview: true });
                          setIsSwatchModalOpen(true);
                        }}
                        className="relative rounded-lg overflow-hidden border-2 border-border hover:border-primary/50 transition-all"
                        title={preview.name}
                        data-testid={`plank-preview-${idx}`}
                      >
                        <div className="aspect-[1/3] w-full">
                          <ZoomableImage
                            src={preview.image}
                            alt={preview.name}
                            className="w-full h-full object-cover object-center"
                            containerClassName="w-full h-full"
                            showHint={false}
                          />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 pointer-events-none">
                          <p className="text-xs text-white truncate">{preview.name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Click to view individual plank
                  </p>
                </div>
              )}

              {/* Single Tile Preview - For Tile products */}
              {product.singleTilePreviews && product.singleTilePreviews.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold mb-3">Single Tile Preview ({product.singleTilePreviews.length})</h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-48 overflow-y-auto pr-2">
                    {product.singleTilePreviews.map((preview, idx) => (
                      <button
                        key={preview.image || preview.name}
                        onClick={() => {
                          setSelectedSwatch({ ...preview, index: idx, isTilePreview: true });
                          setIsSwatchModalOpen(true);
                        }}
                        className="relative aspect-square rounded-lg overflow-hidden border-2 border-border hover:border-primary/50 transition-all"
                        title={preview.name}
                        data-testid={`tile-preview-${idx}`}
                      >
                        <ZoomableImage
                          src={preview.image}
                          alt={preview.name}
                          className="w-full h-full object-cover"
                          containerClassName="w-full h-full"
                          showHint={false}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 pointer-events-none">
                          <p className="text-[10px] text-white truncate leading-tight">{preview.name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Click to view individual tile
                  </p>
                </div>
              )}

              {/* Color Swatches - Grouped by Color Family */}
              {product.swatches && product.swatches.length > 0 && (() => {
                const sorted = sortSwatchesByColorGroup(product.swatches);
                const swatchGroups = groupSwatchesByColor(product.swatches);
                const showGroupLabels = swatchGroups.length > 2 && product.swatches.length > 6;
                return (
                <div className="mb-8">
                  <h3 className="font-semibold mb-3">{product.swatchesLabel || "Available Colors"} ({product.swatches.length})</h3>
                  <div className="max-h-64 overflow-y-auto pr-2">
                    {showGroupLabels ? (
                      swatchGroups.map((group) => (
                        <div key={group.groupId}>
                          <p className="text-[11px] uppercase tracking-wider text-muted-foreground/70 font-medium mb-1.5 mt-3 first:mt-0 border-b border-border/40 pb-1"
                             data-testid={`swatch-group-${group.groupId}`}>
                            {group.groupLabel}
                          </p>
                          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 mb-1">
                            {group.swatches.map((swatch) => {
                              const globalIdx = sorted.findIndex(s => s.name === swatch.name && s.image === swatch.image);
                              return (
                              <button
                                key={swatch.name}
                                onClick={() => {
                                  setSelectedSwatch({ ...swatch, index: globalIdx });
                                  setIsSwatchModalOpen(true);
                                }}
                                className={cn(
                                  "relative aspect-square rounded-lg overflow-hidden border-2 transition-all",
                                  selectedSwatch?.index === globalIdx 
                                    ? "border-primary ring-2 ring-primary ring-offset-2" 
                                    : "border-border hover:border-primary/50"
                                )}
                                title={swatch.name}
                                data-testid={`swatch-${globalIdx}`}
                              >
                                <ZoomableImage
                                  src={swatch.image}
                                  alt={swatch.name}
                                  className="w-full h-full object-cover"
                                  containerClassName="w-full h-full"
                                  showHint={false}
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 pointer-events-none">
                                  <p className="text-[10px] text-white truncate leading-tight">{swatch.name}</p>
                                </div>
                              </button>
                            );})}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                        {sorted.map((swatch, idx) => (
                          <button
                            key={swatch.name}
                            onClick={() => {
                              setSelectedSwatch({ ...swatch, index: idx });
                              setIsSwatchModalOpen(true);
                            }}
                            className={cn(
                              "relative aspect-square rounded-lg overflow-hidden border-2 transition-all",
                              selectedSwatch?.index === idx 
                                ? "border-primary ring-2 ring-primary ring-offset-2" 
                                : "border-border hover:border-primary/50"
                            )}
                            title={swatch.name}
                            data-testid={`swatch-${idx}`}
                          >
                            <ZoomableImage
                              src={swatch.image}
                              alt={swatch.name}
                              className="w-full h-full object-cover"
                              containerClassName="w-full h-full"
                              showHint={false}
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 pointer-events-none">
                              <p className="text-[10px] text-white truncate leading-tight">{swatch.name}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Click a swatch to view larger
                  </p>
                </div>
              );})()}

              {/* Four-Up Tile Sets Gallery - Only for Carpet Tile products */}
              {product.fourUpSwatches && product.fourUpSwatches.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold mb-3">Four-Up Tile Sets ({product.fourUpSwatches.length})</h3>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-48 overflow-y-auto pr-2">
                    {product.fourUpSwatches.map((swatch, idx) => (
                      <button
                        key={swatch.image || swatch.name}
                        onClick={() => {
                          setSelectedSwatch({ ...swatch, index: idx, isFourUp: true });
                          setIsSwatchModalOpen(true);
                        }}
                        className="relative aspect-square rounded-lg overflow-hidden border-2 border-border hover:border-primary/50 transition-all"
                        title={swatch.name}
                        data-testid={`four-up-swatch-${idx}`}
                      >
                        <ZoomableImage
                          src={swatch.image}
                          alt={swatch.name}
                          className="w-full h-full object-cover"
                          containerClassName="w-full h-full"
                          showHint={false}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 pointer-events-none">
                          <p className="text-[10px] text-white truncate leading-tight">{swatch.name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Click to view four-tile configuration
                  </p>
                </div>
              )}

              {/* Attribute Icons - SVG Based */}
              {product.attributeIcons && product.attributeIcons.length > 0 && (
                <div className="flex items-center gap-6 mb-8" data-testid="attribute-icons">
                  {product.attributeIcons.map((icon) => (
                    <img 
                      key={icon.src || icon.alt}
                      src={icon.src} 
                      alt={icon.alt} 
                      className="h-14 w-auto"
                      title={icon.alt}
                    />
                  ))}
                </div>
              )}

              {/* Zone Compatibility - Only show for products with zones */}
              {product.zones && product.zones.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold mb-3">Zone Compatibility</h3>
                  <div className="flex items-center gap-4" data-testid="zone-compatibility">
                    {[1, 2, 3].map((zone) => (
                      <Link
                        key={zone}
                        to="/solutions#zone-defense-section"
                        className={cn(
                          "transition-opacity hover:opacity-80",
                          product.zones.includes(zone) ? "opacity-100" : "opacity-30"
                        )}
                        data-testid={`zone-${zone}-link`}
                      >
                        <img 
                          src={`/Assets/icons/zones/Zone${zone}_icon.svg`}
                          alt={`Zone ${zone}`}
                          className="h-14 w-auto"
                        />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Tabs: Key Features, Specifications, Learn More */}
              <Tabs defaultValue="features" className="w-full">
                <TabsList className="w-full grid grid-cols-3">
                  <TabsTrigger value="features" data-testid="tab-features">Key Features</TabsTrigger>
                  <TabsTrigger value="specifications" data-testid="tab-specifications">Specifications</TabsTrigger>
                  <TabsTrigger value="learn" data-testid="tab-learn">Learn More</TabsTrigger>
                </TabsList>
                
                <ProductFeaturesTab product={product} />

                <ProductSpecificationsTab product={product} />

                <ProductLearnTab product={product} />
              </Tabs>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Accessories Section */}
      <section className="bg-gray-50 border-t border-gray-200">
        <AccessoriesSection 
          title="Complete Your Installation"
          subtitle="Professional-grade accessories for optimal performance"
          showCategoryTabs={true}
          maxItems={4}
          onRequestSample={() => setIsSampleModalOpen(true)}
          onRequestQuote={() => setIsQuoteModalOpen(true)}
        />
      </section>

      {/* View In Space Modal */}
      <Dialog open={isVisualizerOpen} onOpenChange={setIsVisualizerOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>View In Your Space</DialogTitle>
          </DialogHeader>
          <div className="text-center py-8">
            <Eye className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              Room visualization coming soon.
            </p>
            <p className="text-sm text-muted-foreground">
              Our AR visualization feature will allow you to see how {product.name} looks in your actual space.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox Modal */}
      <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden" data-testid="image-lightbox-modal">
          <div className="relative bg-muted">
            {/* Large Image with Zoom */}
            <ZoomableImage
              src={gallery[selectedImageIndex]}
              alt={`${product.name} - View ${selectedImageIndex + 1}`}
              className="w-full h-[70vh] object-contain"
              containerClassName="w-full"
            />
            
            {/* Navigation Arrows */}
            {gallery.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedImageIndex((prev) => (prev - 1 + gallery.length) % gallery.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card/90 rounded-full flex items-center justify-center hover:bg-card transition-colors shadow-lg"
                  data-testid="lightbox-prev-btn"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => setSelectedImageIndex((prev) => (prev + 1) % gallery.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card/90 rounded-full flex items-center justify-center hover:bg-card transition-colors shadow-lg"
                  data-testid="lightbox-next-btn"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            
            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-card/90 px-4 py-2 rounded-full text-sm font-medium">
              {selectedImageIndex + 1} / {gallery.length}
            </div>
          </div>
          
          {/* Thumbnails */}
          {gallery.length > 1 && (
            <div className="p-4 bg-background border-t border-border">
              <div className="flex gap-2 justify-center overflow-x-auto">
                {gallery.map((img, idx) => (
                  <button
                    key={img || `gallery-${idx}`}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={cn(
                      "flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all",
                      selectedImageIndex === idx 
                        ? "border-primary ring-2 ring-primary ring-offset-2" 
                        : "border-transparent hover:border-border"
                    )}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Swatch Viewer Modal */}
      {selectedSwatch && (
        <Dialog open={isSwatchModalOpen} onOpenChange={(open) => {
          setIsSwatchModalOpen(open);
          if (!open) setIsPlankFullscreen(false);
        }}>
          {/* ─── PLANK-SPECIFIC INSPECTION MODAL ─── */}
          {selectedSwatch.isPlankPreview ? (
            <DialogContent 
              className={cn(
                "p-0 overflow-hidden transition-all duration-300",
                isPlankFullscreen 
                  ? "max-w-[98vw] w-[98vw] h-[95vh] max-h-[95vh]" 
                  : "max-w-6xl w-[95vw] h-[85vh] max-h-[85vh]"
              )} 
              data-testid="swatch-modal"
            >
              <div className="flex flex-col h-full">
                {/* Top bar: name + controls */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-card flex-shrink-0 relative z-10">
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-base truncate" data-testid="plank-name">
                      {selectedSwatch.name}
                    </h3>
                    <p className="text-xs text-muted-foreground truncate">
                      {product.name} — Single Plank Preview
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <span className="text-xs text-muted-foreground hidden sm:block">
                      {selectedSwatch.index + 1} / {product.singlePlankPreviews.length}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setIsPlankFullscreen(prev => !prev);
                      }}
                      className="h-8 w-8 p-0"
                      title={isPlankFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                      data-testid="plank-fullscreen-btn"
                    >
                      {isPlankFullscreen 
                        ? <Minimize2 className="w-4 h-4" /> 
                        : <Maximize2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                {/* Main content: image + sidebar */}
                <div className="flex flex-1 min-h-0">
                  {/* Image inspection area */}
                  <div className="flex-1 relative bg-muted min-w-0 min-h-0 overflow-hidden">
                    {/* Prev arrow */}
                    <button
                      onClick={() => {
                        const newIndex = selectedSwatch.index > 0 
                          ? selectedSwatch.index - 1 
                          : product.singlePlankPreviews.length - 1;
                        setSelectedSwatch({ 
                          ...product.singlePlankPreviews[newIndex], 
                          index: newIndex, isPlankPreview: true 
                        });
                      }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
                      data-testid="swatch-prev-btn"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    {/* Plank image with background-image zoom */}
                    <PlankZoomImage
                      src={selectedSwatch.image}
                      alt={selectedSwatch.name}
                    />

                    {/* Next arrow */}
                    <button
                      onClick={() => {
                        const newIndex = selectedSwatch.index < product.singlePlankPreviews.length - 1 
                          ? selectedSwatch.index + 1 
                          : 0;
                        setSelectedSwatch({ 
                          ...product.singlePlankPreviews[newIndex], 
                          index: newIndex, isPlankPreview: true 
                        });
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
                      data-testid="swatch-next-btn"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Compact sidebar with thumbnails */}
                  <div className="hidden md:flex flex-col w-48 border-l border-border bg-card flex-shrink-0">
                    <div className="p-3 border-b border-border">
                      <p className="text-xs font-medium text-muted-foreground mb-0.5">All Planks</p>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                      {product.singlePlankPreviews.map((plank, idx) => (
                        <button
                          key={`modal-${plank.image || plank.name}`}
                          onClick={() => setSelectedSwatch({ 
                            ...plank, index: idx, isPlankPreview: true 
                          })}
                          className={cn(
                            "w-full flex items-center gap-2 p-1.5 rounded-lg transition-all text-left",
                            selectedSwatch.index === idx 
                              ? "bg-primary/10 ring-1 ring-primary" 
                              : "hover:bg-muted"
                          )}
                          data-testid={`plank-thumb-${idx}`}
                        >
                          <div className="w-7 h-16 rounded overflow-hidden flex-shrink-0 border border-border">
                            <img
                              src={plank.image}
                              alt={plank.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className={cn(
                            "text-xs truncate",
                            selectedSwatch.index === idx ? "font-medium text-primary" : "text-muted-foreground"
                          )}>
                            {plank.name}
                          </span>
                        </button>
                      ))}
                    </div>
                    <div className="p-3 border-t border-border">
                      <Button 
                        size="sm"
                        className="w-full text-xs" 
                        data-testid="request-sample-btn"
                        onClick={() => {
                          setIsSwatchModalOpen(false);
                          setIsPlankFullscreen(false);
                          setIsSampleModalOpen(true);
                        }}
                      >
                        Request Sample
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Mobile bottom bar */}
                <div className="md:hidden flex items-center justify-between px-3 py-2.5 border-t border-border bg-card flex-shrink-0">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => {
                      const newIndex = selectedSwatch.index > 0 
                        ? selectedSwatch.index - 1 
                        : product.singlePlankPreviews.length - 1;
                      setSelectedSwatch({ ...product.singlePlankPreviews[newIndex], index: newIndex, isPlankPreview: true });
                    }}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {selectedSwatch.index + 1} / {product.singlePlankPreviews.length}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => {
                      setIsSwatchModalOpen(false);
                      setIsSampleModalOpen(true);
                    }}
                  >
                    Request Sample
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => {
                      const newIndex = selectedSwatch.index < product.singlePlankPreviews.length - 1 
                        ? selectedSwatch.index + 1 : 0;
                      setSelectedSwatch({ ...product.singlePlankPreviews[newIndex], index: newIndex, isPlankPreview: true });
                    }}
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </DialogContent>
          ) : (
          /* ─── STANDARD SWATCH MODAL (unchanged) ─── */
          <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="swatch-modal">
            <div className="flex flex-col md:flex-row">
              {/* Large Swatch Image with Zoom */}
              <div className={`flex-1 bg-muted ${selectedSwatch.isPlankPreview ? 'flex items-center justify-center p-4' : ''}`}>
                <ZoomableImage
                  src={selectedSwatch.image}
                  alt={selectedSwatch.name}
                  className={selectedSwatch.isPlankPreview 
                    ? "max-h-[450px] w-auto object-contain" 
                    : "w-full h-64 md:h-[450px] object-cover"
                  }
                  containerClassName={selectedSwatch.isPlankPreview ? '' : 'w-full'}
                />
              </div>
              
              {/* Swatch Info & Navigation */}
              <div className="w-full md:w-72 p-6 flex flex-col">
                <DialogHeader className="mb-4">
                  <DialogTitle className="font-heading text-xl">
                    {selectedSwatch.name}
                  </DialogTitle>
                  <p className="text-sm text-muted-foreground">
                    {product.name} - {product.variant}
                  </p>
                  {selectedSwatch.isFourUp && (
                    <p className="text-xs text-primary mt-1">Four-Tile Configuration</p>
                  )}
                  {selectedSwatch.isPlankPreview && (
                    <p className="text-xs text-primary mt-1">Single Plank Preview</p>
                  )}
                  {selectedSwatch.isTilePreview && (
                    <p className="text-xs text-primary mt-1">Single Tile Preview</p>
                  )}
                </DialogHeader>
                
                {/* Navigation between swatches */}
                <div className="flex items-center justify-between mb-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const swatchArray = selectedSwatch.isFourUp 
                        ? product.fourUpSwatches 
                        : selectedSwatch.isPlankPreview 
                          ? product.singlePlankPreviews 
                          : selectedSwatch.isTilePreview 
                            ? product.singleTilePreviews 
                            : sortSwatchesByColorGroup(product.swatches);
                      const newIndex = selectedSwatch.index > 0 
                        ? selectedSwatch.index - 1 
                        : swatchArray.length - 1;
                      setSelectedSwatch({ 
                        ...swatchArray[newIndex], 
                        index: newIndex, 
                        isFourUp: selectedSwatch.isFourUp,
                        isPlankPreview: selectedSwatch.isPlankPreview,
                        isTilePreview: selectedSwatch.isTilePreview 
                      });
                    }}
                    data-testid="swatch-prev-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {selectedSwatch.index + 1} / {(
                      selectedSwatch.isFourUp 
                        ? product.fourUpSwatches 
                        : selectedSwatch.isPlankPreview 
                          ? product.singlePlankPreviews 
                          : selectedSwatch.isTilePreview 
                            ? product.singleTilePreviews 
                            : product.swatches
                    ).length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const swatchArray = selectedSwatch.isFourUp 
                        ? product.fourUpSwatches 
                        : selectedSwatch.isPlankPreview 
                          ? product.singlePlankPreviews 
                          : selectedSwatch.isTilePreview 
                            ? product.singleTilePreviews 
                            : sortSwatchesByColorGroup(product.swatches);
                      const newIndex = selectedSwatch.index < swatchArray.length - 1 
                        ? selectedSwatch.index + 1 
                        : 0;
                      setSelectedSwatch({ 
                        ...swatchArray[newIndex], 
                        index: newIndex, 
                        isFourUp: selectedSwatch.isFourUp,
                        isPlankPreview: selectedSwatch.isPlankPreview,
                        isTilePreview: selectedSwatch.isTilePreview 
                      });
                    }}
                    data-testid="swatch-next-btn"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                
                {/* Quick swatch thumbnails */}
                <div className="flex-1">
                  <p className="text-sm font-medium mb-3">
                    {selectedSwatch.isFourUp 
                      ? "All Four-Tile Sets" 
                      : selectedSwatch.isPlankPreview 
                        ? "All Planks" 
                        : selectedSwatch.isTilePreview 
                          ? "All Tiles" 
                          : "All Colors"}
                  </p>
                  <div className="grid grid-cols-5 gap-2 max-h-48 overflow-y-auto pr-1">
                    {(selectedSwatch.isFourUp 
                      ? product.fourUpSwatches 
                      : selectedSwatch.isPlankPreview 
                        ? product.singlePlankPreviews 
                        : selectedSwatch.isTilePreview 
                          ? product.singleTilePreviews 
                          : sortSwatchesByColorGroup(product.swatches)
                    ).map((swatch, idx) => (
                      <button
                        key={`swatch-list-${swatch.image || swatch.name}`}
                        onClick={() => setSelectedSwatch({ 
                          ...swatch, 
                          index: idx, 
                          isFourUp: selectedSwatch.isFourUp,
                          isPlankPreview: selectedSwatch.isPlankPreview,
                          isTilePreview: selectedSwatch.isTilePreview
                        })}
                        className={cn(
                          "rounded overflow-hidden border-2 transition-all relative",
                          selectedSwatch.isPlankPreview ? "aspect-[1/4]" : "aspect-square",
                          selectedSwatch.index === idx 
                            ? "border-primary ring-1 ring-primary" 
                            : "border-border hover:border-primary/50"
                        )}
                        title={swatch.name}
                      >
                        <img
                          src={swatch.image}
                          alt={swatch.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1 py-0.5 pointer-events-none">
                          <p className="text-[8px] text-white truncate leading-tight">{swatch.name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Request Sample CTA */}
                <div className="mt-6 pt-4 border-t border-border">
                  <Button 
                    className="w-full" 
                    data-testid="request-sample-btn"
                    onClick={() => {
                      setIsSwatchModalOpen(false);
                      setIsSampleModalOpen(true);
                    }}
                  >
                    Request Sample
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
          )}
        </Dialog>
      )}

      {/* QuickShip Sample Request Modal */}
      <Dialog open={isSampleModalOpen} onOpenChange={setIsSampleModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quickship-sample-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Package className="w-5 h-5 text-primary" />
              QuickShip Sample Request
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Product Info */}
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">Requesting sample for:</p>
              <p className="font-semibold">{product?.name}</p>
              {selectedSwatch && (
                <p className="text-sm text-muted-foreground">Color: {selectedSwatch.name}</p>
              )}
            </div>

            {/* Form Fields */}
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sample-name">Full Name *</Label>
                  <Input 
                    id="sample-name" 
                    placeholder="John Smith"
                    value={sampleFormData.name}
                    onChange={(e) => setSampleFormData({...sampleFormData, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sample-email">Email *</Label>
                  <Input 
                    id="sample-email" 
                    type="email"
                    placeholder="john@company.com"
                    value={sampleFormData.email}
                    onChange={(e) => setSampleFormData({...sampleFormData, email: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sample-company">Company *</Label>
                  <Input 
                    id="sample-company" 
                    placeholder="Company Name"
                    value={sampleFormData.company}
                    onChange={(e) => setSampleFormData({...sampleFormData, company: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sample-phone">Phone *</Label>
                  <Input 
                    id="sample-phone" 
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={sampleFormData.phone}
                    onChange={(e) => setSampleFormData({...sampleFormData, phone: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sample-address">Shipping Address *</Label>
                <Input 
                  id="sample-address" 
                  placeholder="123 Main Street"
                  value={sampleFormData.address}
                  onChange={(e) => setSampleFormData({...sampleFormData, address: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sample-city">City *</Label>
                  <Input 
                    id="sample-city" 
                    placeholder="City"
                    value={sampleFormData.city}
                    onChange={(e) => setSampleFormData({...sampleFormData, city: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sample-state">State *</Label>
                  <Input 
                    id="sample-state" 
                    placeholder="State"
                    value={sampleFormData.state}
                    onChange={(e) => setSampleFormData({...sampleFormData, state: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sample-zip">ZIP Code *</Label>
                  <Input 
                    id="sample-zip" 
                    placeholder="12345"
                    value={sampleFormData.zip}
                    onChange={(e) => setSampleFormData({...sampleFormData, zip: e.target.value})}
                  />
                </div>
              </div>

              {/* Sample Set Selection */}
              <div className="space-y-2">
                <Label>Select Sample Set *</Label>
                <div className="grid grid-cols-3 gap-3">
                  {sampleSetOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setSampleFormData({...sampleFormData, sampleSet: option.id})}
                      className={cn(
                        "p-3 rounded-lg border-2 text-left transition-all duration-200",
                        sampleFormData.sampleSet === option.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50 hover:bg-muted/50"
                      )}
                    >
                      <p className="font-medium text-sm">{option.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">{option.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sample-notes">Additional Notes</Label>
                <Textarea 
                  id="sample-notes" 
                  placeholder="Any specific requirements or questions..."
                  rows={3}
                  value={sampleFormData.notes}
                  onChange={(e) => setSampleFormData({...sampleFormData, notes: e.target.value})}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setIsSampleModalOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1"
                data-testid="submit-sample-request-btn"
                onClick={async () => {
                  // Required-field validation (Company + Phone now required)
                  const missing = [];
                  if (!sampleFormData.company.trim()) missing.push('Company');
                  if (!sampleFormData.phone.trim()) missing.push('Phone');
                  if (missing.length) {
                    alert(`Please fill in required fields: ${missing.join(', ')}`);
                    return;
                  }
                  try {
                    const res = await fetch(`/api/quick-ship-samples`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        name: sampleFormData.name,
                        email: sampleFormData.email,
                        company: sampleFormData.company,
                        phone: sampleFormData.phone,
                        productName: product?.name || '',
                        colorSelection: selectedSwatch?.name || '',
                        notes: `Sample Set: ${sampleFormData.sampleSet}\nAddress: ${sampleFormData.address}, ${sampleFormData.city}, ${sampleFormData.state} ${sampleFormData.zip}\n${sampleFormData.notes}`,
                      }),
                    });
                    if (!res.ok) throw new Error('Submit failed');
                    setIsSampleModalOpen(false);
                    setSampleFormData({
                      name: '', email: '', company: '', phone: '',
                      address: '', city: '', state: '', zip: '', sampleSet: '', notes: ''
                    });
                  } catch (err) {
                    console.error('Sample request error:', err);
                    alert("We couldn't submit your sample request. Please try again, or email samples@porticosystems.com directly.");
                  }
                }}
              >
                Submit Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quote Modal (triggered from Accessory CTAs) */}
      <IndustryQuoteModal
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
      />
    </Layout>
  );
};

export default ProductDetailPage;
